/**
 * Customer team relationship operations with member + invitation support
 */

import {
  CustomerTeam,
  CustomerTeamDetail,
  CustomerTeamInvitation,
  CustomerTeamMember,
  CustomerTeamRole,
} from './types';
import { mockCustomers } from './base';

const makeMember = (overrides: Partial<CustomerTeamMember>): CustomerTeamMember => ({
  id: `member-${Math.random().toString(36).slice(2, 10)}`,
  name: 'Unnamed Member',
  email: 'member@example.com',
  role: 'member',
  status: 'active',
  joined_at: new Date().toISOString(),
  last_active: new Date().toISOString(),
  avatar_url: undefined,
  ...overrides,
});

const makeInvitation = (overrides: Partial<CustomerTeamInvitation>): CustomerTeamInvitation => ({
  id: `invite-${Math.random().toString(36).slice(2, 10)}`,
  email: 'pending@example.com',
  role: 'member',
  invited_by: 'System',
  invited_at: new Date().toISOString(),
  expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
  status: 'pending',
  message: undefined,
  ...overrides,
});

const mockTeamDetails: Record<string, CustomerTeamDetail[]> = {
  'customer-uuid-1': [
    {
      id: 'team-uuid-1',
      name: 'Acme Automation Core',
      role: 'owner',
      total_seats: 20,
      monthly_spend: 599.99,
      description: 'Primary workspace coordinating automation scenarios for the Acme product squads.',
      created_at: '2023-05-10T08:30:00Z',
      last_active: '2024-11-12T14:20:00Z',
      members: 0,
      membersList: [
        makeMember({
          id: 'member-1',
          name: 'Jamie Rivera',
          email: 'jamie@acme.com',
          role: 'owner',
          status: 'active',
          joined_at: '2022-05-01T09:00:00Z',
          last_active: '2024-11-15T09:42:00Z',
        }),
        makeMember({
          id: 'member-2',
          name: 'Priya Patel',
          email: 'priya@acme.com',
          role: 'admin',
          status: 'active',
          joined_at: '2022-07-18T11:45:00Z',
          last_active: '2024-11-14T18:12:00Z',
        }),
        makeMember({
          id: 'member-3',
          name: 'Marcus Lee',
          email: 'marcus@acme.com',
          role: 'member',
          status: 'active',
          joined_at: '2023-01-09T10:05:00Z',
          last_active: '2024-11-13T07:55:00Z',
        }),
        makeMember({
          id: 'member-4',
          name: 'Anaïs Dupont',
          email: 'anais@acme.com',
          role: 'member',
          status: 'active',
          joined_at: '2023-11-02T12:20:00Z',
          last_active: '2024-11-12T21:30:00Z',
        }),
      ],
      invitations: [
        makeInvitation({
          id: 'invite-1',
          email: 'alex@acme.com',
          role: 'admin',
          invited_by: 'Jamie Rivera',
          invited_at: '2024-11-10T12:00:00Z',
          expires_at: '2024-11-17T12:00:00Z',
          status: 'pending',
        }),
        makeInvitation({
          id: 'invite-2',
          email: 'susan@acme.com',
          role: 'member',
          invited_by: 'Priya Patel',
          invited_at: '2024-10-28T15:30:00Z',
          expires_at: '2024-11-04T15:30:00Z',
          status: 'expired',
        }),
      ],
      pending_invitations: 0,
    },
    {
      id: 'team-uuid-2',
      name: 'Marketing Agency',
      role: 'member',
      total_seats: 10,
      monthly_spend: 199.99,
      description: 'Partner workspace shared with Acme’s external marketing firm.',
      created_at: '2023-09-14T10:00:00Z',
      last_active: '2024-11-08T16:40:00Z',
      members: 0,
      membersList: [
        makeMember({
          id: 'member-5',
          name: 'Louis Campos',
          email: 'louis@agency.com',
          role: 'owner',
          status: 'active',
          joined_at: '2023-03-12T08:45:00Z',
        }),
        makeMember({
          id: 'member-6',
          name: 'Jamie Rivera',
          email: 'jamie@acme.com',
          role: 'member',
          status: 'active',
          joined_at: '2023-04-02T10:15:00Z',
        }),
      ],
      invitations: [],
      pending_invitations: 0,
    },
  ],
  'customer-uuid-3': [
    {
      id: 'team-uuid-3',
      name: 'GlobalCorp Engineering',
      role: 'owner',
      total_seats: 30,
      monthly_spend: 1999.99,
      description: 'Engineering excellence center coordinating platform integrations.',
      created_at: '2022-02-20T09:30:00Z',
      last_active: '2024-11-14T08:05:00Z',
      members: 0,
      membersList: [
        makeMember({
          id: 'member-7',
          name: 'Sarah Johnson',
          email: 'sarah@gcorp.com',
          role: 'owner',
          joined_at: '2022-02-20T09:30:00Z',
        }),
        makeMember({
          id: 'member-8',
          name: 'Daniel Kim',
          email: 'daniel@gcorp.com',
          role: 'admin',
          joined_at: '2022-03-02T12:15:00Z',
        }),
        makeMember({
          id: 'member-9',
          name: 'Yuko Tanaka',
          email: 'yuko@gcorp.com',
          role: 'member',
          joined_at: '2023-05-06T14:55:00Z',
        }),
        makeMember({
          id: 'member-10',
          name: 'Carlos Mendes',
          email: 'carlos@gcorp.com',
          role: 'member',
          joined_at: '2023-11-12T07:35:00Z',
        }),
      ],
      invitations: [
        makeInvitation({
          id: 'invite-3',
          email: 'newhire@gcorp.com',
          role: 'member',
          invited_by: 'Sarah Johnson',
          invited_at: '2024-11-11T09:12:00Z',
          expires_at: '2024-11-18T09:12:00Z',
          status: 'pending',
        }),
      ],
      pending_invitations: 0,
    },
  ],
  'customer-uuid-4': [
    {
      id: 'team-uuid-4',
      name: 'Innovative Tech Solutions',
      role: 'owner',
      total_seats: 12,
      monthly_spend: 299.99,
      description: 'Product experimentation workspace for proof-of-concept flows.',
      created_at: '2023-04-05T15:05:00Z',
      last_active: '2024-11-09T19:22:00Z',
      members: 0,
      membersList: [
        makeMember({
          id: 'member-11',
          name: 'Amina Solis',
          email: 'amina@innovativetech.io',
          role: 'owner',
          joined_at: '2023-04-05T15:05:00Z',
        }),
        makeMember({
          id: 'member-12',
          name: 'Greg Howard',
          email: 'greg@innovativetech.io',
          role: 'member',
          joined_at: '2023-06-28T10:48:00Z',
        }),
      ],
      invitations: [],
      pending_invitations: 0,
    },
  ],
  'customer-uuid-5': [
    {
      id: 'team-uuid-5',
      name: 'DataDriven Analytics',
      role: 'owner',
      total_seats: 5,
      monthly_spend: 0,
      description: 'Legacy workspace maintained for historical reporting.',
      created_at: '2023-01-11T11:20:00Z',
      last_active: '2024-08-14T06:18:00Z',
      members: 0,
      membersList: [
        makeMember({
          id: 'member-13',
          name: 'Helena Ortiz',
          email: 'helena@datadriven.ai',
          role: 'owner',
          joined_at: '2023-01-11T11:20:00Z',
        }),
      ],
      invitations: [],
      pending_invitations: 0,
    },
  ],
};

function recalculateTeam(detail: CustomerTeamDetail): CustomerTeamDetail {
  const activeMembers = detail.membersList.filter((member) => member.status !== 'invited');
  detail.members = activeMembers.length;
  detail.pending_invitations = detail.invitations.filter((invite) => invite.status === 'pending').length;
  return detail;
}

function summarizeTeam(detail: CustomerTeamDetail): CustomerTeam {
  const recalculated = recalculateTeam(detail);
  return {
    id: recalculated.id,
    name: recalculated.name,
    role: recalculated.role,
    members: recalculated.members,
    total_seats: recalculated.total_seats,
    monthly_spend: recalculated.monthly_spend,
    description: recalculated.description,
    created_at: recalculated.created_at,
    last_active: recalculated.last_active,
    pending_invitations: recalculated.pending_invitations,
  };
}

function getTeamDetail(customerId: string, teamId: string): {
  customerIndex: number;
  teamIndex: number;
  team: CustomerTeamDetail;
} {
  const customerIndex = mockCustomers.findIndex((c) => c.id === customerId);
  if (customerIndex === -1) {
    throw {
      response: {
        status: 404,
        data: {
          message: 'Customer not found',
        },
      },
    };
  }

  const teams = mockTeamDetails[customerId] || [];
  const teamIndex = teams.findIndex((t) => t.id === teamId);
  if (teamIndex === -1) {
    throw {
      response: {
        status: 404,
        data: {
          message: 'Team not found',
        },
      },
    };
  }

  return { customerIndex, teamIndex, team: teams[teamIndex] };
}

function findTeamById(teamId: string): { customerId: string; team: CustomerTeamDetail } | null {
  for (const [customerId, teams] of Object.entries(mockTeamDetails)) {
    const team = teams.find((entry) => entry.id === teamId);
    if (team) {
      return { customerId, team };
    }
  }

  return null;
}

function snapshot<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function ensureCustomerTeams(customerId: string): CustomerTeamDetail[] {
  if (!mockTeamDetails[customerId]) {
    mockTeamDetails[customerId] = [];
  }
  return mockTeamDetails[customerId];
}

function refreshCustomerSummary(customerId: string) {
  const customerIndex = mockCustomers.findIndex((c) => c.id === customerId);
  if (customerIndex === -1) {
    return;
  }

  const teams = ensureCustomerTeams(customerId);
  mockCustomers[customerIndex].teams = teams.map((team) => summarizeTeam(team));
}

Object.entries(mockTeamDetails).forEach(([customerId, teams]) => {
  teams.forEach(recalculateTeam);
  refreshCustomerSummary(customerId);
});

mockCustomers.forEach((customer) => {
  ensureCustomerTeams(customer.id);
  refreshCustomerSummary(customer.id);
});

export const teamOperations = {
  dashboard: (customerId: string) => {
    const teams = ensureCustomerTeams(customerId).map((team) => snapshot(recalculateTeam(team)));

    const stats = teams.reduce(
      (acc, team) => {
        acc.totalSeats += team.total_seats;
        acc.usedSeats += team.members;
        acc.pendingInvitations += team.pending_invitations ?? 0;
        acc.activeMembers += team.members;
        return acc;
      },
      { totalSeats: 0, usedSeats: 0, pendingInvitations: 0, activeMembers: 0 }
    );

    return {
      data: {
        stats,
        teams,
      },
    };
  },

  list: (customerId: string): { data: CustomerTeam[] } => {
    refreshCustomerSummary(customerId);
    const customer = mockCustomers.find((c) => c.id === customerId);
    if (!customer) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Customer not found',
          },
        },
      };
    }
    return { data: customer.teams };
  },

  getById: (customerId: string, teamId: string): { data: CustomerTeamDetail } => {
    const { team } = getTeamDetail(customerId, teamId);
    return { data: snapshot(recalculateTeam(team)) };
  },

  create: (
    customerId: string,
    data: {
      name: string;
      total_seats: number;
      role?: CustomerTeamRole;
      description?: string;
    }
  ): { data: CustomerTeamDetail } => {
    const teams = ensureCustomerTeams(customerId);
    const newTeam: CustomerTeamDetail = recalculateTeam({
      id: `team-${Date.now()}`,
      name: data.name,
      role: data.role ?? 'owner',
      members: 0,
      total_seats: data.total_seats,
      monthly_spend: 0,
      description: data.description,
      created_at: new Date().toISOString(),
      last_active: new Date().toISOString(),
      membersList: [],
      invitations: [],
      pending_invitations: 0,
    });

    teams.push(newTeam);
    refreshCustomerSummary(customerId);
    return { data: snapshot(newTeam) };
  },

  update: (
    customerId: string,
    teamId: string,
    data: Partial<{
      name: string;
      description: string;
      total_seats: number;
      monthly_spend: number;
    }>
  ): { data: CustomerTeamDetail } => {
    const { team } = getTeamDetail(customerId, teamId);
    Object.assign(team, data);
    recalculateTeam(team);
    refreshCustomerSummary(customerId);
    return { data: snapshot(team) };
  },

  delete: (customerId: string, teamId: string): { message: string } => {
    const teams = ensureCustomerTeams(customerId);
    const index = teams.findIndex((team) => team.id === teamId);
    if (index === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Team not found',
          },
        },
      };
    }

    if (teams[index].role !== 'owner') {
      throw {
        response: {
          status: 403,
          data: {
            message: 'Only team owners can delete teams',
          },
        },
      };
    }

    teams.splice(index, 1);
    refreshCustomerSummary(customerId);
    return { message: 'Team deleted successfully' };
  },

  leave: (customerId: string, teamId: string): { message: string } => {
    const { team } = getTeamDetail(customerId, teamId);
    if (team.role === 'owner') {
      throw {
        response: {
          status: 400,
          data: {
            message: 'Transfer ownership before leaving the team.',
          },
        },
      };
    }

    const teams = ensureCustomerTeams(customerId);
    const index = teams.findIndex((t) => t.id === teamId);
    teams.splice(index, 1);
    refreshCustomerSummary(customerId);
    return { message: 'Successfully left team' };
  },

  addMember: (
    customerId: string,
    teamId: string,
    data: { email: string; role?: CustomerTeamRole; message?: string }
  ): { data: CustomerTeamInvitation } => {
    const { team } = getTeamDetail(customerId, teamId);

    if (team.members >= team.total_seats) {
      throw {
        response: {
          status: 400,
          data: {
            message: 'Team has reached maximum capacity',
          },
        },
      };
    }

    const alreadyMember = team.membersList.some((member) => member.email.toLowerCase() === data.email.toLowerCase());
    if (alreadyMember) {
      throw {
        response: {
          status: 409,
          data: {
            message: 'This user is already a member of the team',
          },
        },
      };
    }

    const existingInvitation = team.invitations.find(
      (invite) => invite.email.toLowerCase() === data.email.toLowerCase() && invite.status === 'pending'
    );
    if (existingInvitation) {
      throw {
        response: {
          status: 409,
          data: {
            message: 'An invitation has already been sent to this email',
          },
        },
      };
    }

    const invitation: CustomerTeamInvitation = makeInvitation({
      email: data.email,
      role: data.role ?? 'member',
      invited_by: team.membersList.find((member) => member.role === 'owner')?.name ?? 'Team Owner',
      message: data.message,
    });
    team.invitations.push(invitation);
    recalculateTeam(team);

    refreshCustomerSummary(customerId);
    return { data: snapshot(invitation) };
  },

  removeMember: (customerId: string, teamId: string, memberId: string): { message: string } => {
    const { team } = getTeamDetail(customerId, teamId);

    if (!['owner', 'admin'].includes(team.role)) {
      throw {
        response: {
          status: 403,
          data: {
            message: 'Only team owners or admins can remove members',
          },
        },
      };
    }

    const memberIndex = team.membersList.findIndex((member) => member.id === memberId);
    if (memberIndex === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Team member not found',
          },
        },
      };
    }

    const member = team.membersList[memberIndex];
    if (member.role === 'owner') {
      throw {
        response: {
          status: 400,
          data: {
            message: 'Transfer ownership before removing the current owner',
          },
        },
      };
    }

    team.membersList.splice(memberIndex, 1);
    recalculateTeam(team);
    refreshCustomerSummary(customerId);

    return { message: 'Member removed successfully' };
  },
  removeMemberByTeamId: (teamId: string, memberId: string): { message: string } => {
    const match = findTeamById(teamId);
    if (!match) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Team not found',
          },
        },
      };
    }

    return teamOperations.removeMember(match.customerId, teamId, memberId);
  },

  updateMemberRole: (
    customerId: string,
    teamId: string,
    memberId: string,
    data: { role: CustomerTeamRole }
  ): { data: CustomerTeamMember } => {
    const { team } = getTeamDetail(customerId, teamId);

    const member = team.membersList.find((item) => item.id === memberId);
    if (!member) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Team member not found',
          },
        },
      };
    }

    member.role = data.role;
    member.status = 'active';
    recalculateTeam(team);
    refreshCustomerSummary(customerId);

    return { data: snapshot(member) };
  },

  resendInvitation: (customerId: string, teamId: string, invitationId: string): { data: CustomerTeamInvitation } => {
    const { team } = getTeamDetail(customerId, teamId);

    const invitation = team.invitations.find((invite) => invite.id === invitationId);
    if (!invitation) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Invitation not found',
          },
        },
      };
    }

    invitation.invited_at = new Date().toISOString();
    invitation.expires_at = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    invitation.status = 'pending';
    recalculateTeam(team);
    refreshCustomerSummary(customerId);

    return { data: snapshot(invitation) };
  },

  cancelInvitation: (customerId: string, teamId: string, invitationId: string): { message: string } => {
    const { team } = getTeamDetail(customerId, teamId);

    const invitationIndex = team.invitations.findIndex((invite) => invite.id === invitationId);
    if (invitationIndex === -1) {
      throw {
        response: {
          status: 404,
          data: {
            message: 'Invitation not found',
          },
        },
      };
    }

    team.invitations[invitationIndex].status = 'revoked';
    recalculateTeam(team);
    refreshCustomerSummary(customerId);

    return { message: 'Invitation revoked' };
  },
};
